## Python jako kalkulator
Uruchom IDLE shell. Jeśli z niego nie korzystasz, to włącz terminal i uruchom Pythona w konsoli, wpisując: 

```bash
python3
```

Możemy używać teraz Pythona, tak samo jakbyśmy używali kalkulatora:

```python
>>> 2 + 5
7
>>> 2 * 9 - 15.5
2.5
>>> 2 * (9 - 15.5)
-13.0
>>> 2 * 2 + 2
6
>>> 2 + 2 * 2 
6
```
Zwróć uwagę na to, że Python zna kolejność wykonywania działań (mnożenie wykonuje się przed dodawaniem), więc nie musisz pisać zbędnych nawiasów. 

## Operatory
W Pythonie wyróżniamy następujące operatory: <br/>
* **+** - dodawanie <br/>
* **-** - odejmowanie <br/>
* ***** - mnożenie <br/>
* **/** - dzielenie <br/>
* **//** - dzielenie całkowite <br/>
* ****** - potęgowanie <br/>
* **%** - modulo (reszta z dzielenia) <br/>

```python
>>> 9 / 2
4.5
>>> 9 // 2 
4
>>> 2 ** 3 
8
>>> 9 % 2
1
```

## Typy

W Pythonie wyróżniamy cztery podstawowe (proste) typy danych:
* `int` - liczby całkowite
* `float` - liczby zmiennoprzecinkowe
* `bool` - typ logiczny - prawda/fałsz
* `string` - napisy

### Typy liczbowe

W podtematach "Python jako kalkulator" oraz "Operatory" wykonywaliśmy operacje na dwóch typach liczbowych - `int` oraz `float`. 

Float'y, czyli liczby zmiennoprzecinkowe zapisujemy z kropką, a nie z przecinkiem!

```python
>>> 2.0
2.0 
>>> 4.5
4.5
>>> 4,5
(4,5) 
# w ten sposób utworzyliśmy krotkę
# więcej o nich dowiemy się w następnym wykładzie!
```

Warto zwrócić uwagę na to, że mimo iż są to dwa różne typy, to możemy wykonać jakieś działanie arytmetyczne, np. dodać floata do inta bez potrzeby konwersji z jednego typu na drugi. Python robi to za nas. 

```python
>>> 4 + 4.5 
8.5
```

Wynik dowolnego działania zawierającego floata i inta jest typu float. 

Co ciekawe wynikiem dzielenia (`/`) dwóch liczb jest również float (nawet jeśli dzielimy dwa int'y, a wynik nie ma części dziesiętnej)

```python
>>> 8 / 2
4.0 
```

### Bool
Zmienna boolowska może przyjąć  jedną z dwóch wartości logicznych - True (prawda)  lub False (fałsz). Zwróć uwagę na to, że zapisujemy je z dużych liter.

```python
>>> True
True
>>> False
False
```
Co się stanie, kiedy spróbujemy użyć jednego z operatorów arytmetycznych na zmiennych boolowskich? 
```python
>>> True * True
1 
>>> True * False
0
>>> True + True
2
>>> False - True
-1
```
Dzieje się tak dlatego, że pod spodem True kryje się tak naprawdę 1, a pod False 0. 

### String
Stringi, czyli napisy zapisujemy za pomocą cudzysłowu `""` lub za pomocą apostrofów `''`. 

```python
>>> 'Ala ma kota'
'Ala ma kota'
>>> "Ala ma kota"
"Ala ma kota"
>>> "Ala ma kota'
SyntaxError: unterminated string literal (detected at line 1)
>>> Ala ma kota
SyntaxError: invalid syntax
```
Nie ma znaczenie czy używamy cudzysłowu, czy apostrofów — ważne jest jednak, żeby każdy string kończył się tym samym znakiem, od którego się zaczął — w przeciwnym wypadku interpreter zwróci błąd. Radzimy również, aby w całym programie używać albo tylko cudzysłowu, albo tylko apostrofów — zwiększy to czytelność kodu. <br/><br/>


Na stringach możemy wykonać dwa rodzaje działań: 
* **dodawanie dwóch napisów** (konkatenacja)
* **mnożenie napisu przez liczbę**

```python
>>> 'Ala ma ' + 'kota'
'Ala ma kota'
>>> 'Ala' * 5
'AlaAlaAlaAlaAla'
```

A co w przypadku, kiedy chcemy dodać liczbę do napisu? 

```python
>>> 'Mam ' + '18' + ' lat'
'Mam 18 lat'
>>> 'Mam' + 18 + 'lat'
Traceback (most recent call last):
  File "<pyshell#33>", line 1, in <module>
    'Mam' + 18 + 'lat'
TypeError: can only concatenate str (not "int") to str
```

Otrzymujemy błąd, ponieważ nie jest możliwe bezpośrednie dodanie inta do stringa. 
Najpierw musimy dokonać konwersji, czyli zmienić typ inta. Używamy do tego celu funkcji str().

```python
>>> 'Mam ' + str(18) + ' lat'
'Mam 18 lat'
```

### Konwersja między typami
Przed chwilą poznaliśmy pojęcie funkcji, lecz czym właściwie ona jest?
W skrócie funkcja jest wydzielonym kawałkiem kodu, który możemy używać wiele razy w dowolnych miejscach programu. Funkcja może przyjmować argumenty i zwracać pewną wartość. 
Np. funkcja **str()**, którą poznaliśmy wcześniej, przyjmuje jako argument liczbę, a zwraca napis. Funkcje możemy zdefiniować sami - czym zajmiemy się w dalszym wykładzie — lub skorzystać z wielu funkcji wbudowanych, które są dostępne w języku Python.<br/><br/>

Funkcje, na których się dzisiaj skupimy, służą do konwersji między typami:
* **str()** - zmienia typ obiektu na string
* **int()** - zmienia typ obiektu na int
* **float()** - zmienia typ obiektu na float
```python=
>>> str(18.5)
'18.5'
>>> int('18')
18
>>> int(17.5)
17
>>> float(3)
3.0
>>>float('1')
1.0
```

### type()
To też jest jedna z funkcji wbudowanych, która służy do sprawdzania typu obiektu.  

```python
>>> type(10)
<class 'int'>
>>> type(10.0)
<class 'float'>
>>> type(True)
<class 'bool'>
>>> type(False)
<class 'bool'>
>>> type("Hello world!")
<class 'str'>
```